import {
  businessLabelsSchema,
  businessRulesSchema,
  refundSchema,
  type Business,
  type BusinessLabels,
  type BusinessRules,
  type Refund,
  type RefundRequest,
  type UpdateBusinessLabelsRequest,
} from "@teamna/shared";
import { useMutation, useQueryClient, type UseMutationResult } from "@tanstack/react-query";
import { api, newIdempotencyKey } from "../api/client.ts";
import { queryKeys } from "./keys.ts";

/**
 * The writes of the slice E office pages: the cancellation rule on its own, the business's words,
 * and a refund. Kept apart from `use-owner.ts` so the pages that own them change one file.
 */

const ownerBase = (businessId: string) => `/owner/businesses/${businessId}`;

/**
 * PUT /business/businesses/:id/rules
 *
 * The rule alone. The setup step saves it with the whole package list; the Packages and rules page
 * edits one package at a time, so the rule has its own save.
 */
export function useSaveRules(
  businessId: string,
): UseMutationResult<BusinessRules, unknown, BusinessRules> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (rules) =>
      api.put(`/business/businesses/${businessId}/rules`, rules, businessRulesSchema),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.setup.all(businessId) });
    },
  });
}

/**
 * PATCH /owner/businesses/:id/labels
 *
 * The answer is the ten words, so it is written straight into the cached business: every screen
 * reads its words through `useLabels()`, which reads that cache, and the sidebar changes at once.
 */
export function useUpdateLabels(
  businessId: string,
): UseMutationResult<BusinessLabels, unknown, UpdateBusinessLabelsRequest> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input) => api.patch(`${ownerBase(businessId)}/labels`, input, businessLabelsSchema),
    onSuccess: (labels) => {
      queryClient.setQueryData<Business>(queryKeys.owner.business(businessId), (current) =>
        current ? { ...current, labels } : current,
      );
      void queryClient.invalidateQueries({ queryKey: queryKeys.owner.business(businessId) });
    },
  });
}

/** POST /owner/businesses/:id/payments/:paymentId/refund. No amount refunds what is left. */
export function useRefundPayment(
  businessId: string,
): UseMutationResult<Refund, unknown, { paymentId: string; idempotencyKey?: string } & RefundRequest> {
  const queryClient = useQueryClient();
  return useMutation({
    // The panel passes one key while it is open, so pressing Refund again after a lost answer
    // replays the first refund instead of recording a second one.
    mutationFn: ({ paymentId, idempotencyKey, ...body }) =>
      api.post(`${ownerBase(businessId)}/payments/${paymentId}/refund`, body, refundSchema, {
        idempotencyKey: idempotencyKey ?? newIdempotencyKey(),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.owner.payments(businessId) });
    },
  });
}
